/**
 * Iter 57.1 — Reusable PWA install hook.
 *
 * Returns:
 *   { canInstall, isStandalone, isIOS, promptInstall }
 *
 *   canInstall      Chrome has fired `beforeinstallprompt` → tap the button to install.
 *   isStandalone    User is already running the PWA (don't show install UI).
 *   isIOS           Safari/iOS — must guide the user to Share → Add to Home Screen manually.
 *   promptInstall() Triggers the saved Chrome install prompt.
 */
import { useEffect, useState } from 'react';

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Standalone mode? Then the app is already installed.
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true; // iOS
    setIsStandalone(standalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;

  const promptInstall = async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return result.outcome; // 'accepted' | 'dismissed'
  };

  return { canInstall: !!deferredPrompt, isStandalone, isIOS, promptInstall };
}

/**
 * Call once at app boot to register the service worker.
 * Failures are swallowed — PWA install is a progressive enhancement.
 */
export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Use a short delay so SW registration doesn't block first paint.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}
